import { IonContent, IonPage, IonAlert } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { utilService } from "../../services";
import { MyPatientsObj, MyPatientsResponse } from "./MyPatientsDataModels";
import { MyPatientsService } from "./MyPatientsService";
import Loader from "../../components/Shared/Loader/Loader";
import "./MyPatients.scss";

const MyPatients: React.FC = () => {
  const { t } = useTranslation("patients");
  const [patientsList, setPatientsList] = useState<any[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({
    isOpen: false,
    title: "",
    message: "",
  });

  useEffect(() => {
    getPatientsList();
  }, []);

  const getPatientsList = async () => {
    const currentDate = new Date();
    let reqParams: { [key: string]: any } = {
      reg_code: utilService.getNetwork(),
      hour: currentDate.getHours(),
      minute: currentDate.getMinutes(),
      date: `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`,
    };

    setShowLoader(true);
    try {
      const response: MyPatientsResponse = await MyPatientsService.getPatients(reqParams);
      if (response && response.isSuccess && response.result?.length) {
        const mappedBackendPatients = response.result.map((item: MyPatientsObj, index: number) => ({
          id: item.id || item.patientDetails?.id || index,
          bedNumber: item.bedNumber || item.patientDetails?.bedNumber,
          patient_name: item.patientDetails?.patient_name,
          unit_name: item.unitName || item.unit?.unitName,
          nurse_name: item.nurseContactDetails?.name,
          contact_number: item.patientDetails?.contact_details?.[0]?.number,
        }));
        setPatientsList(mappedBackendPatients);
      } else {
        setPatientsList([]);
      }
    } catch (err) {
      console.warn("Failed to load patients list", err);
      setPatientsList([]);
    } finally {
      setShowLoader(false);
    }
  };

  const handleCallWard = (patient: any) => {
    setAlertConfig({
      isOpen: true,
      title: t("callWardTitle", "Call Ward - Bed {{bed}}", { bed: patient.bedNumber }),
      message: t("callWardMsg", "Initiating call to {{unit}} Ward (Nurse: {{nurse}}). Contact: {{contact}}", {
        unit: patient.unit_name || "",
        nurse: patient.nurse_name || "",
        contact: patient.contact_number || "",
      }),
    });
  };

  return (
    <IonPage>
      <IonContent className="ion-no-padding">
        <div className="patients-page-container">
          {/* Main Patients Content */}
          <div className="patients-content">
            <div className="patients-main-header">
              <div className="header-titles">
                <h1>{t("patientsTitle", "Patients")}</h1>
                <p>{t("patientsSubtitle", "Your patients, the ward boards and open cases, in one shell.")}</p>
              </div>
            </div>

            {showLoader && (
              <div className="loader-container my-4">
                <Loader />
              </div>
            )}

            <div className="section-container">
              <div className="section-header-bar">
                <span className="section-title">{t("cardiologyTitle", "My patients - Cardiology")}</span>
                <span className="section-badge">
                  {t("bedsCount", "{{count}} beds", { count: patientsList.length })}
                </span>
              </div>

              <div className="section-cards-list">
                {patientsList.map((patient) => (
                  <div key={patient.id} className="patient-card-row">
                    <div className="card-left-group">
                      <div className="bed-badge">{patient.bedNumber}</div>
                      <div className="patient-details">
                        <h3 className="patient-name">{patient.patient_name}</h3>
                        <p className="patient-meta">
                          {patient.unit_name}, {t("bed", "bed")} {patient.bedNumber} ·{" "}
                          <span className="nurse-highlight">
                            {t("nurse", "nurse")} {patient.nurse_name}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="card-right-group">
                      <button
                        className="action-btn-ward"
                        onClick={() => handleCallWard(patient)}
                      >
                        {t("callWard", "Call the ward")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Feedback Alert */}
          <IonAlert
            isOpen={alertConfig.isOpen}
            onDidDismiss={() => setAlertConfig({ ...alertConfig, isOpen: false })}
            header={alertConfig.title}
            message={alertConfig.message}
            buttons={[t("ok", "OK")]}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default MyPatients;
